import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { cva, cn } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import * as React from 'react';
import { useState } from 'react';
import { useEvent } from 'src/hooks/useEvent';

const checkboxVariants = cva(
    'peer flex shrink-0 cursor-pointer items-center justify-center rounded-xs border border-primary p-px ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            size: {
                small: ['h-3 w-3'],
                medium: ['h-4 w-4'],
            },
            checkedState: {
                primary: 'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
                secondary:
                    'border-2 data-[state=checked]:border-steel-400 data-[state=checked]:bg-black data-[state=checked]:text-steel-400',
            },
        },
        defaultVariants: {
            size: 'medium',
            checkedState: 'primary',
        },
    },
);

export interface CheckboxProps
    extends
        Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, 'onCheckedChange'>,
        VariantProps<typeof checkboxVariants> {}

interface TwoWayCheckboxProps extends Omit<CheckboxProps, 'onCheckedChange'> {
    threeWayCheckbox?: false;

    onCheckedChange?(checked: boolean): void;
}

interface ThreeWayCheckboxProps extends Omit<CheckboxProps, 'onCheckedChange'> {
    threeWayCheckbox: true;

    onCheckedChange?(checked: boolean | undefined): void;
}

type CheckboxPropsUnion = TwoWayCheckboxProps | ThreeWayCheckboxProps;

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxPropsUnion>(
    (
        {
            checked,
            defaultChecked,
            className,
            children,
            checkedState,
            size,
            onCheckedChange,
            threeWayCheckbox = false,
            ...props
        },
        ref,
    ) => {
        const isControlled = checked !== undefined;
        const [internalChecked, setInternalChecked] = useState<CheckedState>(
            threeWayCheckbox ? (checked ?? defaultChecked ?? 'indeterminate') : (checked ?? defaultChecked ?? false),
        );
        const currentChecked: CheckedState = isControlled
            ? threeWayCheckbox
                ? (checked as CheckedState)
                : (checked as boolean)
            : internalChecked;

        const handleCheckedChange = useEvent((value: CheckedState) => {
            if (threeWayCheckbox) {
                let newValue: CheckedState;
                if (currentChecked === true) {
                    newValue = false;
                } else if (currentChecked === false) {
                    newValue = 'indeterminate';
                } else {
                    newValue = true;
                }
                setInternalChecked(newValue);
                (onCheckedChange as ThreeWayCheckboxProps['onCheckedChange'])?.(
                    newValue === 'indeterminate' ? undefined : newValue,
                );
            } else {
                setInternalChecked(value as boolean);
                (onCheckedChange as TwoWayCheckboxProps['onCheckedChange'])?.(value as boolean);
            }
        });

        return (
            <CheckboxPrimitive.Root
                ref={ref}
                className={cn(
                    checkboxVariants({ size, checkedState }),
                    threeWayCheckbox &&
                        'data-[state=unchecked]:border-red-500 data-[state=unchecked]:bg-red-500 data-[state=unchecked]:text-white',
                    className,
                )}
                onCheckedChange={handleCheckedChange}
                checked={currentChecked}
                {...props}
            >
                <CheckboxPrimitive.Indicator
                    className={cn('flex h-full w-full items-center justify-center text-current')}
                >
                    {threeWayCheckbox && currentChecked === true && (
                        <FontAwesomeIcon icon={faCheck} className="h-full w-full" />
                    )}
                    {!threeWayCheckbox && (children || <FontAwesomeIcon icon={faCheck} className="h-full w-full" />)}
                </CheckboxPrimitive.Indicator>
                {/* The indicator is not rendered when the value is false, so we render it outside */}
                {threeWayCheckbox && currentChecked === false && (
                    <FontAwesomeIcon icon={faXmark} className="h-full w-full" />
                )}
            </CheckboxPrimitive.Root>
        );
    },
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
export { Checkbox };
